/**
 * My Telling Tale — Asset Manifest
 * Central source of truth for all visual assets
 * 
 * RULES:
 * - NO 3rd party image URLs allowed
 * - Only reference files from /src/assets/ directory
 * - Missing images will show as grayscale placeholders
 * 
 * TO UPLOAD YOUR IMAGES:
 * 1. Replace placeholder files in /src/assets/ subdirectories
 * 2. Use exact file names specified below
 * 3. Refer to device size recommendations in Illustration Library
 */

// ============================================
// BRAND IDENTITY
// ============================================

// Logo - Primary Full Color
// File: figma:asset (uploaded by user)
import LogoPrimary from 'figma:asset/01cb13ea6bac03554f736781231275670509647a.png';

// ============================================
// FEATURE ILLUSTRATIONS
// ============================================

// Voice Recording Phone
// File: figma:asset (uploaded by user)
import VoiceRecordingPhone from 'figma:asset/642ecd33fdf84e1aa13901e30fde84281581abc2.png';

// Before/After Emotional Journey
// File: figma:asset (uploaded by user)
import BeforeAfterJourney from 'figma:asset/651ad906ee43487efa8e2562db1336f116fb7c09.png';

// Before/After Emotional Journey - Before State
// File: figma:asset (uploaded by user)
import BeforeStateAlone from 'figma:asset/d35a4c51ac98fd771d7bf16b8b96875900354ec2.png';

// Before/After Emotional Journey - After State
// File: figma:asset (uploaded by user)
import AfterStateFamily from 'figma:asset/febdfd191e6159510cc7869c366735d890d4ace3.png';

// Welcome / Onboarding - Community Storytelling
// File: figma:asset (uploaded by user)
// Dimensions: 375px × 1102px (optimized for mobile welcome screen)
import WelcomeCommunity from 'figma:asset/f5fa03a27e0a76dfe5db23a1c0b7ea1cfb07f6d6.png';

// ============================================
// CHARACTER ILLUSTRATIONS
// ============================================

// Multi-Generational Family Group
// File: figma:asset (uploaded by user)
import FamilyGroupMultigen from 'figma:asset/6343db85b259fc32f3d32e0bf1790a5c3b134f04.png';

// Grandparents with Grandchildren
// File: figma:asset (uploaded by user)
import GrandparentsGrandchildren from 'figma:asset/d4177943f4cc29aeb8f8e59bf0b18ab7eba964e0.png';

// ============================================
// DECORATIVE ACCENTS
// ============================================

// Plant Accent - Leaf Branch (Eucalyptus)
// File: figma:asset (uploaded by user)
import PlantLeafBranch from 'figma:asset/80be6b94b0e47e60b7e599c1c0dd0a8c48d3f72f.png';

// Plant Accent - Three Leaves
// File: figma:asset (uploaded by user)
import PlantThreeLeaves from 'figma:asset/a76b8b0c108890ca45d2f70c4d30314be873f151.png';

// ============================================
// DECORATIVE SHAPES (Atmospheric)
// ============================================

// Eclipse Blob - Orange
// File: figma:asset (uploaded by user)
import EclipseBlobOrange from 'figma:asset/2c39b7b5a5931bead5fe15869040e22bfe87a8df.png';

// Eclipse Blob - Yellow
// File: figma:asset (uploaded by user)
import EclipseBlobYellow from 'figma:asset/a96b5cff03247ca6f52df25d51fb7eb932d895d5.png';

// Kidney Blob - Yellow
// File: figma:asset (uploaded by user)
import KidneyBlobYellow from 'figma:asset/9b091ee798a59c527d958de64a929e0e69a34136.png';

// Loop Stroke - Yellow (SVG preferred)
// File: /src/assets/shapes/loop-stroke-yellow.svg
import LoopStrokeYellow from './shapes/loop-stroke-yellow.svg';

// ============================================
// WORKFLOW ELEMENTS
// ============================================

// Workflow Number Units (1, 2, 3)
// Files: /src/assets/workflow/number-1.png, number-2.png, number-3.png
import WorkflowNumber1 from './workflow/number-1.png';
import WorkflowNumber2 from './workflow/number-2.png';
import WorkflowNumber3 from './workflow/number-3.png';

// ============================================
// EXPORT MANIFEST
// ============================================

export const AssetManifest = {
  // Brand Identity
  brand: {
    logoPrimary: LogoPrimary,
  },

  // Feature Illustrations
  features: {
    voiceRecordingPhone: VoiceRecordingPhone,
    beforeAfterJourney: BeforeAfterJourney,
    beforeStateAlone: BeforeStateAlone,
    afterStateFamily: AfterStateFamily,
    welcomeCommunity: WelcomeCommunity,
  },

  // Character Illustrations
  characters: {
    familyGroupMultigen: FamilyGroupMultigen,
    grandparentsGrandchildren: GrandparentsGrandchildren,
  },

  // Decorative Accents
  decorative: {
    plantLeafBranch: PlantLeafBranch,
    plantThreeLeaves: PlantThreeLeaves,
  },

  // Decorative Shapes
  shapes: {
    eclipseBlobOrange: EclipseBlobOrange,
    eclipseBlobYellow: EclipseBlobYellow,
    kidneyBlobYellow: KidneyBlobYellow,
    loopStrokeYellow: LoopStrokeYellow,
  },

  // Workflow Elements
  workflow: {
    number1: WorkflowNumber1,
    number2: WorkflowNumber2,
    number3: WorkflowNumber3,
  },
};

// Type-safe asset access
export type AssetCategory = keyof typeof AssetManifest;
export type AssetKey<T extends AssetCategory> = keyof typeof AssetManifest[T];